import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { evaluateEevee } from '../utils/pokemonMaths';

const EeveelutionModal = ({ isOpen, onClose, selectedMons, onRemove }) => {
  const [sortMethod, setSortMethod] = useState('tier');

  if (!isOpen || !selectedMons || selectedMons.length === 0) return null;

  const tierOrder = { "S": 5, "A": 4, "B": 3, "C": 2, "F": 1 };

  const sortedMons = [...selectedMons].map(mon => ({
    mon,
    evalResult: evaluateEevee(mon)
  })).sort((a, b) => {
    if (sortMethod === 'tier') {
      const tierDiff = (tierOrder[b.evalResult.tier] || 0) - (tierOrder[a.evalResult.tier] || 0);
      if (tierDiff !== 0) return tierDiff;
    }
    return b.mon.level - a.mon.level;
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="bg-gray-800 p-3 md:p-4 flex justify-between items-center text-white shrink-0 shadow-md z-20">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-pink-400" />
            <h2 className="text-lg md:text-xl font-black tracking-tight">Eeveelution Analyser</h2>
          </div>
          <div className="flex items-center gap-3">
            <select 
              value={sortMethod} 
              onChange={(e) => setSortMethod(e.target.value)}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1 text-sm outline-none hidden md:block cursor-pointer focus:ring-2 focus:ring-pink-500 font-bold"
            >
              <option value="tier">Sort by Tier</option>
              <option value="level">Sort by Level</option>
            </select>
            <button 
              onClick={onClose} 
              className="p-1.5 md:p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Sort */}
        <div className="md:hidden bg-gray-50 border-b border-gray-200 p-2 flex justify-end shrink-0">
            <select 
              value={sortMethod} 
              onChange={(e) => setSortMethod(e.target.value)}
              className="bg-white border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm outline-none w-full font-bold cursor-pointer focus:ring-2 focus:ring-pink-500 shadow-sm"
            >
              <option value="tier">Sort by Tier</option>
              <option value="level">Sort by Level</option>
            </select>
        </div>

        {/* Content Body */}
        <div className="p-4 overflow-y-auto flex-1 bg-gray-50">
          <div className="grid gap-4 md:grid-cols-2">
            {sortedMons.map(({ mon, evalResult }) => {
              return (
                <div key={mon.uuid} className="p-4 rounded-xl border border-gray-200 shadow-sm bg-white flex flex-col h-full relative">
                  
                  {onRemove && (
                    <button 
                      onClick={() => onRemove(mon.uuid)}
                      className="absolute top-2 right-2 p-1.5 bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 rounded-lg transition-colors z-10"
                      title="Remove from analysis"
                    >
                      <X size={14} />
                    </button>
                  )}
                  
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-3 pr-8">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className={`font-bold text-lg capitalize ${mon.is_shiny ? 'text-indigo-600' : 'text-gray-800'}`}>
                          {mon.nickname || mon.species_id.replace('_', ' ')}
                        </h3>
                        {mon.is_shiny && <Sparkles size={16} className="text-indigo-600 fill-indigo-100 shrink-0" />}
                      </div>
                  <p className="text-xs text-gray-500 font-medium">
                    Lv. {mon.level || 1} • {mon.nature}
                    {mon.date_met && ` • 🗓️ ${mon.date_met}`}
                  </p>
                    </div>
                    <span className={`px-3 py-1 rounded-lg font-bold text-sm border ${evalResult.colorClasses}`}>
                      Tier: {evalResult.tier}
                    </span>
                  </div>

                  {/* Recommendations */}
                  <div className="space-y-3 flex-1">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Recommended</p>
                      <div className="flex flex-wrap gap-1.5">
                        {evalResult.recommendations.map(rec => (
                          <span key={rec} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-sm font-semibold border border-gray-200">
                            {rec}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Notes Section */}
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-auto">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Analysis Notes</p>
                      <ul className="list-disc pl-4 space-y-1 marker:text-gray-400">
                        {evalResult.notes.map((note, idx) => (
                          <li key={idx} className="text-sm text-gray-600 leading-snug">{note}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EeveelutionModal;